// src/Studio/patterns/PatternAnimLayer.tsx
// Renders animated pattern elements as inline SVG overlays above the Konva canvas.
// Only renders elements where isPattern === true AND animated === true.

import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';
import type { Element } from '../types';
import { getAnimatedPatternById, type PatternConfig, DEFAULT_PATTERN_CONFIG } from './PatternEngine';

/** Extra fields stored on pattern image elements beyond the standard Element shape. */
interface PatternElementExtras {
  isPattern: true;
  animated: boolean;
  patternId: string;
  patternConfig: PatternConfig;
}

type PatternAnimElement = Element & PatternElementExtras;

function isPatternAnimElement(el: Element): el is PatternAnimElement {
  const e = el as unknown as Record<string, unknown>;
  return e['isPattern'] === true && e['animated'] === true && e['visible'] !== false;
}

interface PatternAnimLayerProps {
  elements: Element[];
  canvasX: number;        // Canvas stage left offset in screen px
  canvasY: number;        // Canvas stage top offset in screen px
  canvasScale: number;    // Zoom scale factor
  canvasW: number;        // Canvas logical width
  canvasH: number;        // Canvas logical height
}

export function PatternAnimLayer({
  elements,
  canvasX,
  canvasY,
  canvasScale,
  canvasW,
  canvasH,
}: PatternAnimLayerProps) {
  const animatedPatterns = useMemo(
    () => elements.filter(isPatternAnimElement),
    [elements]
  );

  if (animatedPatterns.length === 0) return null;

  return (
    <div
      style={{
        position: 'absolute',
        left: canvasX,
        top: canvasY,
        width: canvasW * canvasScale,
        height: canvasH * canvasScale,
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: 10,
      }}
    >
      {animatedPatterns.map((el) => {
        const def = getAnimatedPatternById(el.patternId);
        if (!def) return null;
        const cfg: PatternConfig = { ...DEFAULT_PATTERN_CONFIG, ...el.patternConfig };
        const elAny = el as unknown as Record<string, number>;
        const elW = Math.round((elAny['width'] ?? canvasW) * canvasScale);
        const elH = Math.round((elAny['height'] ?? canvasH) * canvasScale);
        const elX = (elAny['x'] ?? 0) * canvasScale;
        const elY = (elAny['y'] ?? 0) * canvasScale;
        const elRot: number = elAny['rotation'] ?? 0;
        const svgString = def.getAnimatedSVG(cfg, elW, elH);
        return (
          <div
            key={el.id}
            style={{
              position: 'absolute',
              left: elX,
              top: elY,
              width: elW,
              height: elH,
              mixBlendMode: (cfg.blendMode ?? 'normal') as React.CSSProperties['mixBlendMode'],
              transform: elRot ? `rotate(${elRot}deg)` : undefined,
              transformOrigin: 'center center',
            }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(svgString, { USE_PROFILES: { svg: true, svgFilters: true } }) }}
          />
        );
      })}
    </div>
  );
}
